"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { formatAddress } from "@/helpers/Helpers";
import type { ReactElement } from "react";
import { type EnsAddressPreview, type EnsContenthash, type EnsContentPreview, type EnsLinks, type EnsNamePreview, type EnsPreview } from "./types";
type RecordKey = keyof EnsNamePreview["records"];
const RECORD_LABELS: Readonly<Record<RecordKey, string>> = {
  url: "Website",
  email: "Email",
  "com.twitter": "Twitter",
  "org.telegram": "Telegram",
  "com.github": "GitHub",
  description: "Description"
};
const formatTimestamp = (value: number | null): string | null => {
  if (!value) {
    return null;
  }
  const date = new Date(value * 1000);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
};
const renderAvatar = (url: string | null, alt: string): ReactElement | null => {
  if (!url) {
    return null;
  }
  return <div className="tw-relative tw-h-14 tw-w-14 tw-overflow-hidden tw-rounded-xl tw-bg-iron-800/60">
      <Image src={url} alt={alt} width={112} height={112} className="tw-h-full tw-w-full tw-object-cover" unoptimized />
    </div>;
};
const renderLink = (href: string | undefined, label: string): ReactElement | null => {
  if (!href) {
    return null;
  }
  return <Link key={label} href={href} target="_blank" rel="noopener noreferrer" className="tw-text-sm tw-font-semibold tw-text-primary-300 tw-transition-colors hover:tw-text-primary-100">
      {label}
    </Link>;
};
const renderContenthash = (contenthash: EnsContenthash | null, links: EnsLinks): ReactElement | null => {
  if (!contenthash) {
    return null;
  }
  const {
    protocol,
    decoded,
    gatewayUrl
  } = contenthash;
  return <div className="tw-flex tw-flex-col tw-gap-1">
      <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-400">
        Contenthash
      </span>
      <span className="tw-text-sm tw-text-iron-100">
        {protocol === "other" ? "Other" : protocol.toUpperCase()}
      </span>
      {decoded && <span className="tw-break-all tw-text-xs tw-text-iron-300">
          {decoded}
        </span>}
      {gatewayUrl && renderLink(links.open ?? gatewayUrl, "Open content")}
    </div>;
};
const renderRecords = (records: EnsNamePreview["records"]): ReactElement | null => {
  const entries = (Object.keys(RECORD_LABELS) as RecordKey[]).map(key => {
    const label = RECORD_LABELS[key];
    const value = records[key];
    if (!value) {
      return null;
    }
    if (key === "url") {
      return <li key={key} className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-400">
              {label}
            </span>
            <Link href={value} target="_blank" rel="noopener noreferrer" className="tw-break-all tw-text-sm tw-text-primary-300 tw-transition-colors hover:tw-text-primary-100">
              {value}
            </Link>
          </li>;
    }
    return <li key={key} className="tw-flex tw-flex-col tw-gap-1">
          <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-400">
            {label}
          </span>
          <span className="tw-break-words tw-text-sm tw-text-iron-200">
            {value}
          </span>
        </li>;
  }).filter(Boolean) as ReactElement[];
  if (entries.length === 0) {
    return null;
  }
  return <ul className="tw-grid tw-gap-3 md:tw-grid-cols-2">{entries}</ul>;
};
const renderOwnership = (ownership: EnsNamePreview["ownership"]): ReactElement | null => {
  const {
    registryOwner,
    registrant,
    isWrapped,
    expiry,
    gracePeriodEnds
  } = ownership;
  if (!registryOwner && !registrant && !expiry && !isWrapped) {
    return null;
  }
  const rows: Array<{
    label: string;
    value: string;
  }> = [];
  rows.push({
    label: "Wrapped",
    value: isWrapped ? "Yes" : "No"
  });
  if (registryOwner) {
    rows.push({
      label: "Registry owner",
      value: formatAddress(registryOwner)
    });
  }
  if (registrant) {
    rows.push({
      label: "Registrant",
      value: formatAddress(registrant)
    });
  }
  const expiryDisplay = formatTimestamp(expiry);
  if (expiryDisplay) {
    rows.push({
      label: "Expiry",
      value: expiryDisplay
    });
  }
  const graceDisplay = formatTimestamp(gracePeriodEnds ?? null);
  if (graceDisplay) {
    rows.push({
      label: "Grace period ends",
      value: graceDisplay
    });
  }
  return <div className="tw-flex tw-flex-col tw-gap-2">
      <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-400">
        Ownership
      </span>
      <dl className="tw-grid tw-gap-2 md:tw-grid-cols-2">
        {rows.map(row => <div key={row.label} className="tw-flex tw-flex-col">
            <dt className="tw-text-[0.65rem] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-iron-500">
              {row.label}
            </dt>
            <dd className="tw-text-sm tw-text-iron-100">{row.value}</dd>
          </div>)}
      </dl>
    </div>;
};
const NameCard = t0 => {
  const $ = _c(41);
  const {
    preview
  } = t0;
  const {
    name,
    normalized,
    address,
    avatarUrl,
    links,
    records,
    contenthash,
    ownership
  } = preview;
  let t1;
  if ($[0] !== address) {
    t1 = address ? formatAddress(address) : null;
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedAddress = t1;
  const t2 = `${name} avatar`;
  let t3;
  if ($[2] !== avatarUrl || $[3] !== t2) {
    t3 = renderAvatar(avatarUrl, t2);
    $[2] = avatarUrl;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-primary-300">ENS Name</span>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== name) {
    t5 = <span className="tw-text-xl tw-font-semibold tw-text-iron-50">{name}</span>;
    $[6] = name;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== name || $[9] !== normalized) {
    t6 = normalized !== name && <span className="tw-text-xs tw-text-iron-400">{normalized}</span>;
    $[8] = name;
    $[9] = normalized;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== formattedAddress || $[12] !== links.etherscan) {
    t7 = formattedAddress && <span className="tw-text-sm tw-text-iron-200">{links.etherscan ? <Link href={links.etherscan} target="_blank" rel="noopener noreferrer" className="tw-text-primary-300 tw-transition-colors hover:tw-text-primary-100">{formattedAddress}</Link> : formattedAddress}</span>;
    $[11] = formattedAddress;
    $[12] = links.etherscan;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t8 = <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">{t4}{t5}{t6}{t7}</div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t3 || $[19] !== t8) {
    t9 = <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-start">{t3}{t8}</div>;
    $[18] = t3;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== records) {
    t10 = renderRecords(records);
    $[21] = records;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== contenthash || $[24] !== links) {
    t11 = renderContenthash(contenthash, links);
    $[23] = contenthash;
    $[24] = links;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== ownership) {
    t12 = renderOwnership(ownership);
    $[26] = ownership;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== links.app) {
    t13 = renderLink(links.app, "View in ENS App");
    $[28] = links.app;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== links.etherscan) {
    t14 = renderLink(links.etherscan, "View on Etherscan");
    $[30] = links.etherscan;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t13 || $[33] !== t14) {
    t15 = <div className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-2">{t13}{t14}</div>;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t15 || $[39] !== t9) {
    t16 = <div className="tw-flex tw-flex-col tw-gap-5" data-testid="ens-name-card">{t9}{t10}{t11}{t12}{t15}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t15;
    $[39] = t9;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
};
const AddressCard = t0 => {
  const $ = _c(30);
  const {
    preview
  } = t0;
  const {
    address,
    primaryName,
    forwardMatch,
    avatarUrl,
    links
  } = preview;
  let t1;
  if ($[0] !== address) {
    t1 = formatAddress(address);
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedAddress = t1;
  const t2 = `${address} avatar`;
  let t3;
  if ($[2] !== avatarUrl || $[3] !== t2) {
    t3 = renderAvatar(avatarUrl, t2);
    $[2] = avatarUrl;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-primary-300">ENS Reverse Record</span>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== formattedAddress) {
    t5 = <span className="tw-text-xl tw-font-semibold tw-text-iron-50">{formattedAddress}</span>;
    $[6] = formattedAddress;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== links.app || $[9] !== primaryName) {
    t6 = primaryName && <span className="tw-text-sm tw-text-iron-200">{links.app ? <Link href={links.app} target="_blank" rel="noopener noreferrer" className="tw-text-primary-300 tw-transition-colors hover:tw-text-primary-100">{primaryName}</Link> : primaryName}</span>;
    $[8] = links.app;
    $[9] = primaryName;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = forwardMatch ? "Forward resolution verified" : "Forward resolution not verified";
  let t8;
  if ($[11] !== t7) {
    t8 = <span className="tw-text-xs tw-font-medium tw-text-iron-400">{t7}</span>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t5 || $[14] !== t6 || $[15] !== t8) {
    t9 = <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">{t4}{t5}{t6}{t8}</div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t3 || $[18] !== t9) {
    t10 = <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-start">{t3}{t9}</div>;
    $[17] = t3;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== links.app) {
    t11 = renderLink(links.app, "View in ENS App");
    $[20] = links.app;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== links.etherscan) {
    t12 = renderLink(links.etherscan, "View on Etherscan");
    $[22] = links.etherscan;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11 || $[25] !== t12) {
    t13 = <div className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-2">{t11}{t12}</div>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t10 || $[28] !== t13) {
    t14 = <div className="tw-flex tw-flex-col tw-gap-5" data-testid="ens-address-card">{t10}{t13}</div>;
    $[27] = t10;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
};
const ContentCard = t0 => {
  const $ = _c(17);
  const {
    preview
  } = t0;
  const {
    name,
    contenthash,
    links
  } = preview;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-primary-300">ENS Contenthash</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== name) {
    t2 = <div className="tw-flex tw-flex-col tw-gap-1">{t1}<span className="tw-text-xl tw-font-semibold tw-text-iron-50">{name}</span></div>;
    $[1] = name;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== contenthash || $[4] !== links) {
    t3 = renderContenthash(contenthash, links);
    $[3] = contenthash;
    $[4] = links;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== links.open) {
    t4 = renderLink(links.open, "Open content");
    $[6] = links.open;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== links.app) {
    t5 = renderLink(links.app, "View in ENS App");
    $[8] = links.app;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-2">{t4}{t5}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t6) {
    t7 = <div className="tw-flex tw-flex-col tw-gap-5" data-testid="ens-content-card">{t2}{t3}{t6}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
interface EnsPreviewCardProps {
  readonly preview: EnsPreview;
}
export default function EnsPreviewCard(t0) {
  const $ = _c(6);
  const {
    preview
  } = t0;
  if (preview.type === "ens.address") {
    let t1;
    if ($[0] !== preview) {
      t1 = <AddressCard preview={preview} />;
      $[0] = preview;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (preview.type === "ens.content") {
    let t1;
    if ($[2] !== preview) {
      t1 = <ContentCard preview={preview} />;
      $[2] = preview;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== preview) {
    t1 = <NameCard preview={preview} />;
    $[4] = preview;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}